<template>
  <div class="cif-list-uploader mb-4">
    <div class="d-flex align-center flex-wrap mb-1">
      <span class="text-h6 font-weight-medium mr-2">{{ label }}</span>
      <v-chip size="x-small" color="primary" variant="outlined" class="mr-2">
        {{ files.length }} {{ files.length === 1 ? 'file' : 'files' }} · {{ totalCifCodes }} CIF
      </v-chip>
      <span v-if="hint" class="cif-list-hint">{{ hint }}</span>
    </div>

    <div class="mt-1">
      <v-btn variant="outlined" size="small" color="#4E3982" style="margin-right: 8px" :disabled="!canEdit || uploading" :loading="uploading" @click="triggerUpload">
        <v-icon size="16" class="mr-1">mdi-upload</v-icon>
        Upload File
      </v-btn>
      <v-btn variant="outlined" size="small" color="#4E3982" @click="downloadTemplate">
        <v-icon size="16" class="mr-1">mdi-download</v-icon>
        Download Template
      </v-btn>
    </div>

    <!-- CIF Files Display -->
    <div v-if="files.length > 0" class="mt-3">
      <v-card variant="outlined" class="pa-3">
        <div class="d-flex align-center justify-space-between mb-2">
          <div>
            <v-icon class="mr-2" color="primary" size="20">mdi-file-table</v-icon>
            <span style="font-size: 14px; font-weight: 500">Uploaded CIF Files ({{ files.length }}) - Total CIF Codes: {{ totalCifCodes }}</span>
          </div>
        </div>
        <v-table density="compact" class="modern-table">
          <thead>
            <tr>
              <th class="text-left">File Name</th>
              <th class="text-center" style="width: 100px">CIF Count</th>
              <th class="text-center" style="width: 120px">Upload Date</th>
              <th class="text-center" style="width: 180px">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(file, fileIndex) in files" :key="file.filePath || file.fileName || fileIndex">
              <td class="py-2">
                <div class="d-flex align-center">
                  <v-icon class="mr-2" color="success" size="16">mdi-file-document</v-icon>
                  <div>
                    <div style="font-size: 13px">{{ file.fileName }}</div>
                    <div style="font-size: 11px; color: #666">{{ formatFileSize(file.fileSize) }}</div>
                  </div>
                </div>
              </td>
              <td class="text-center py-2">
                <v-chip size="x-small" color="primary" variant="outlined">
                  {{ file.cifList ? file.cifList.length : 0 }}
                </v-chip>
              </td>
              <td class="text-center py-2" style="font-size: 11px">
                {{ formatUploadDate(file.uploadDate) }}
              </td>
              <td class="text-center py-2">
                <v-btn variant="outlined" color="primary" size="x-small" class="mr-1" @click="downloadFile(file)">
                  <v-icon size="14">mdi-download</v-icon>
                </v-btn>
                <v-btn variant="outlined" color="error" size="x-small" :disabled="!canEdit" @click="removeFile(fileIndex)">
                  <v-icon size="14">mdi-delete</v-icon>
                </v-btn>
              </td>
            </tr>
          </tbody>
        </v-table>
        <div v-if="files.length > 1" class="d-flex justify-end mt-2">
          <v-btn variant="outlined" color="error" size="x-small" :disabled="!canEdit" @click="clearAll">
            <v-icon size="14" class="mr-1">mdi-delete-sweep</v-icon>
            Clear All
          </v-btn>
        </div>
      </v-card>
    </div>

    <input ref="fileInput" type="file" accept=".csv,.xlsx,.xls" style="display: none" @change="handleFileChange" />
  </div>
</template>

<script>
// Reusable CIF list uploader (Whitelist / Blacklist / Available List on the Tax Planning page).
// Same flow as the inline feature/question CIF uploads: client-side type/size/duplicate checks, server-side
// parse through UploadTaxPlanningCif, then the parsed file is kept in memory (v-model) until the page is saved.
const ALLOWED_TYPES = ['text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
const MAX_SIZE = 5 * 1024 * 1024;

export default {
  name: 'CifListUploader',
  props: {
    modelValue: {
      type: Array,
      default: () => [],
    },
    label: {
      type: String,
      required: true,
    },
    hint: {
      type: String,
      default: '',
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      uploading: false,
    };
  },
  computed: {
    files() {
      return Array.isArray(this.modelValue) ? this.modelValue : [];
    },
    totalCifCodes() {
      return this.files.reduce((total, file) => total + (file.cifList ? file.cifList.length : 0), 0);
    },
  },
  methods: {
    triggerUpload() {
      this.$refs.fileInput.click();
    },

    handleFileChange(event) {
      const file = event.target.files[0];
      if (!file) return;

      if (!ALLOWED_TYPES.includes(file.type)) {
        this.$swal.fire({ icon: 'warning', title: 'Invalid File Type', text: 'Please upload only CSV or Excel files' });
        this.resetInput();
        return;
      }

      if (file.size > MAX_SIZE) {
        this.$swal.fire({ icon: 'warning', title: 'File Too Large', text: 'File size should not exceed 5MB' });
        this.resetInput();
        return;
      }

      if (this.files.some((f) => f.fileName === file.name)) {
        this.$swal.fire({
          icon: 'warning',
          title: 'Duplicate File',
          text: 'A file with this name has already been uploaded. Please rename the file or remove the existing one.',
        });
        this.resetInput();
        return;
      }

      const formData = new FormData();
      formData.append('file', file);
      this.uploading = true;

      this.$call.UploadTaxPlanningCif(
        formData,
        (dataResponse) => {
          this.uploading = false;
          this.resetInput();

          if (dataResponse.status === 0) {
            const newCifFile = {
              fileName: dataResponse.data.fileName || file.name,
              filePath: dataResponse.data.fileUrl,
              cifList: dataResponse.data.cifList || [],
              uploadDate: new Date(),
              fileSize: file.size,
            };
            this.$emit('update:modelValue', [...this.files, newCifFile]);

            this.$swal.fire({
              icon: 'success',
              title: 'Success',
              text: `File uploaded successfully. Found ${dataResponse.data.cifCount || 0} CIF codes.`,
              timer: 2000,
              showConfirmButton: false,
            });
          } else {
            this.$swal.fire({ icon: 'error', title: 'Error', text: dataResponse.message || 'Failed to upload file' });
          }
        },
        (errorResource) => {
          this.uploading = false;
          this.resetInput();
          console.error('Error uploading file:', errorResource);
          this.$swal.fire({
            icon: 'error',
            title: 'Error',
            text: errorResource.response?.data?.error || 'Failed to upload file',
          });
        }
      );
    },

    resetInput() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    removeFile(fileIndex) {
      const removed = this.files[fileIndex];
      const next = this.files.filter((_, i) => i !== fileIndex);
      this.$emit('update:modelValue', next);

      this.$swal.fire({
        icon: 'info',
        title: 'File Removed',
        html: `"<strong>${removed ? removed.fileName : ''}</strong>" removed.<br><small style="color: #ff9800;">⚠️ Remember to click Save to confirm changes.</small>`,
        timer: 3500,
        showConfirmButton: false,
        toast: true,
        position: 'top-end',
        timerProgressBar: true,
      });
    },

    clearAll() {
      const fileCount = this.files.length;
      this.$emit('update:modelValue', []);

      this.$swal.fire({
        icon: 'info',
        title: 'All Files Removed',
        html: `<strong>${fileCount} files</strong> removed.<br><small style="color: #ff9800;">⚠️ Remember to click Save to confirm changes.</small>`,
        timer: 3500,
        showConfirmButton: false,
        toast: true,
        position: 'top-end',
        timerProgressBar: true,
      });
    },

    downloadFile(file) {
      if (!file || !file.filePath) {
        this.$swal.fire({ icon: 'error', title: 'Error', text: 'No file available for download' });
        return;
      }

      this.$call.DownloadCifFile(
        file.filePath,
        file.fileName,
        (blobData) => {
          const url = URL.createObjectURL(new Blob([blobData]));
          const link = document.createElement('a');
          link.href = url;
          link.download = file.fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);

          this.$swal.fire({
            icon: 'success',
            title: 'Downloading',
            text: 'File download started successfully!',
            timer: 1500,
            showConfirmButton: false,
          });
        },
        (errorResource) => {
          console.error('Error downloading file:', errorResource);
          this.$swal.fire({ icon: 'error', title: 'Error', text: 'Failed to download file' });
        }
      );
    },

    downloadTemplate() {
      const csvContent = 'cif\n12345678901\n23456789012\n34567890123';
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');

      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'cif_template.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        this.$swal.fire({
          icon: 'success',
          title: 'Template Downloaded',
          text: 'CIF template file has been downloaded successfully!',
          timer: 1500,
          showConfirmButton: false,
        });
      }
    },

    formatFileSize(bytes) {
      if (!bytes) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    formatUploadDate(date) {
      if (!date) return '';
      const d = new Date(date);
      return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },
  },
};
</script>

<style scoped>
.cif-list-hint {
  font-size: 12px;
  color: #666;
}

.modern-table :deep(thead th) {
  font-size: 12px;
  font-weight: 600;
  color: #4e3982;
  background: #f8f6fc;
}

.modern-table :deep(tbody td) {
  font-size: 13px;
}
</style>
